import {getDeclarativeTranslationsMapping} from '../DeclarativeTranslationsMapping';
import {buildDeclarativeTranslator, DeclarativeTranslator} from '../DeclarativeTranslator';
import {getPossibleTypesMappingForSchema} from '../PossibleTypesMapping';
import {GraphQLTypes} from './GraphQLTypes';
import {RESTTypes} from './RESTTypes';

const memoizedTranslators: {[key: string]: DeclarativeTranslator} = {};

function getTranslator<TDeclaration extends keyof RESTTypes>(
  declarationName: TDeclaration
): DeclarativeTranslator {
  if (!memoizedTranslators[declarationName]) {
    const mapping = getDeclarativeTranslationsMapping();
    memoizedTranslators[declarationName] = buildDeclarativeTranslator(
      declarationName,
      mapping.getRESTDeclaration(declarationName),
      mapping.getParsedGraphQLFragmentFromName(`${declarationName}GraphQL`),
      mapping,
      getPossibleTypesMappingForSchema()
    );
  }
  return memoizedTranslators[declarationName];
}

/**
 * Translate a REST object to its GraphQL variation using the declarations in graphql-fragments/
 *
 * @param declarationName e.g. "BioentitySummary", the name of the file without extension in graphql-fragments
 * @param restObj The REST version of the object
 */
export function restToGraphQL<TDeclaration extends keyof RESTTypes>(
  declarationName: TDeclaration,
  restObj: RESTTypes[TDeclaration]
): GraphQLTypes[TDeclaration] {
  // @ts-expect-error
  return getTranslator(declarationName).restToGQL(restObj);
}

/**
 * Translate a GraphQL object to its REST variation using the declarations in graphql-fragments/
 *
 * @param declarationName e.g. "BioentitySummary", the name of the file without extension in graphql-fragments
 * @param graphQLObj The GraphQL version of the object
 */
export function graphQLToREST<TDeclaration extends keyof RESTTypes>(
  declarationName: TDeclaration,
  graphQLObj: GraphQLTypes[TDeclaration]
): RESTTypes[TDeclaration] {
  // @ts-expect-error
  return getTranslator(declarationName).gqlToREST(graphQLObj);
}

/**
 * Translate an array of REST objects to their GraphQL variations using declarations in graphql-fragments/
 *
 * @param declarationName e.g. "BioentitySummary", the name of the file without extension in graphql-fragments
 * @param restArray an array of REST versions of the object
 */
export function restArrayToGraphQL<TDeclaration extends keyof RESTTypes>(
  declarationName: TDeclaration,
  restArray: Array<RESTTypes[TDeclaration]>
): Array<GraphQLTypes[TDeclaration]> {
  const translator = getTranslator(declarationName);
  // @ts-expect-error
  return restArray.map((restObj) => translator.restToGQL(restObj));
}

/**
 * Translate an array of GraphQL objects to their REST variations using declarations in graphql-fragments/
 *
 * @param declarationName e.g. "BioentitySummary", the name of the file without extension in graphql-fragments
 * @param graphQLArray an array of GraphQL versions of the object
 */
export function graphQLArrayToREST<TDeclaration extends keyof RESTTypes>(
  declarationName: TDeclaration,
  graphQLArray: Array<GraphQLTypes[TDeclaration]>
): Array<RESTTypes[TDeclaration]> {
  const translator = getTranslator(declarationName);
  // @ts-expect-error
  return graphQLArray.map((graphQLObj) => translator.gqlToREST(graphQLObj));
}
